<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import CategoryDrawer from '@/views/masters/incident-category/drawer/CategoryDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

// import categoryService from '@/services/quality_category.service'
import CategoryService from '@/services/quality/category.service'

const searchQuery = ref('')
const debouncedSearch = ref('')
const isConfirmDialogVisible = ref(false)

// Debounce search 500ms so typing doesn't fire a request per keystroke
let searchTimeout = null

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedSearch.value = val
    page.value = 1
  }, 500)
})

definePage({
  meta: {
    name: 'master-category',
    subject: 'master.incident-category',
    action: 'read',
  },  
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data){
   
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order.toUpperCase()
}

// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Code',
    key: 'code',
  },
  {
    title: 'Department',
    key: 'department_id',
  },
  {
    title: 'Parent Category',
    key: 'parent_id',
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: categoriesData,
  execute: fetchCategories,
} =   useApi(createUrl('/masters/incident-categories', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const categories = computed(() => categoriesData.value?.data)
const totalCategories = computed(() => categoriesData.value?.meta?.total)

const updateCategory = data => {
  if (data.id){
    // data = { ...data, 
    //   ...{
    //     unit_id: cookieRef('selectedUnit').value,
    //     investigatorUsers: data.investigatorUsers?.map(user => user.id),
    //     investigatorGroups: data.investigatorGroups?.map(group => group.id),
    //     watchers: data.watchers?.map(watcher => watcher.id),
    //     sub_categories: data.sub_categories?.map(subCategory => subCategory.id),
    //   },
    // }

    const formData = {
      ...data,
      unit_id: cookieRef('selectedUnit').value,
      
      // investigatorUsers: data.investigatorUsers?.map(user => user.id),
      // investigatorGroups: data.investigatorGroups?.map(group => group.id),
      // watchers: data.watchers?.map(watcher => watcher.id),
      // sub_categories: data.sub_categories?.map(subCategory => subCategory.id),
    }

    CategoryService.update(formData.id, formData, ()=>
    {
      fetchCategories()
      isCategoryDrawerVisible.value = false
      resetErrors()
    }, onError)
   
  }
}

const saveCategory = data => {
  // data = { ...data, 
  //   ...{
  //     unit_id: cookieRef('selectedUnit').value,
  //     investigatorUsers: data.investigatorUsers?.map(user => user.id),
  //     investigatorGroups: data.investigatorGroups?.map(group => group.id),
  //     watchers: data.watchers?.map(watcher => watcher.id),
  //   } }
  
  const formData = {
    ...data,
    unit_id: cookieRef('selectedUnit').value,

    // investigatorUsers: data.investigatorUsers?.map(user => user.id),
    // investigatorGroups: data.investigatorGroups?.map(group => group.id),
    // watchers: data.watchers?.map(watcher => watcher.id),
  }

  CategoryService.create(formData, ()=>
  {
    fetchCategories()
    isCategoryDrawerVisible.value = false
    resetErrors()
  }, onError)
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
}

const isCategoryDrawerVisible = ref(false)
const categoryDrawerData = ref()
const categoryDeleteData = ref()
const categoryDrawerType = ref('Add')

const showCategory = (categoryData, type) => {
  categoryDrawerData.value = categoryData
  categoryDrawerType.value = type  
  isCategoryDrawerVisible.value = true
}

const deleteCategory = async (confirm, data) => {
  if(data?.id && confirm ){
    await CategoryService.destroy(data.id, () => {
      fetchCategories()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = categoryData => {
  categoryDeleteData.value = categoryData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    categoryDeleteData.value = {}
  }
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap align-center py-4 gap-4">
        <!-- 👉 Search -->
        <div class="category-search-field">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VSpacer />

        <div class="d-flex align-center flex-wrap gap-4">
          <!-- 👉 Export button -->
          <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-screen-share"
          >
            Export
          </VBtn>

          <VBtn
            v-if="$can('create', 'master.category')"
            prepend-icon="tabler-plus"
            @click="showCategory({},'Add')"
          >
            Add New Category
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="categories"
        :items-length="totalCategories"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <button
            type="button"
            class="category-name text-body-1 font-weight-medium text-start"
            @click="showCategory(item, 'View')"
          >
            {{ item.name }}
          </button>
        </template>

        <template #item.code="{ item }">
          <span :class="item.code ? 'font-weight-medium' : 'text-medium-emphasis'">
            {{ item.code || '—' }}
          </span>
        </template>

        <template #item.department_id="{ item }">
          <span :class="item.department?.name ? 'font-weight-medium' : 'text-medium-emphasis'">
            {{ item.department?.name || '—' }}
          </span>
        </template>

        <template #item.parent_id="{ item }">
          <span :class="item.parent?.name ? 'font-weight-medium' : 'text-medium-emphasis'">
            {{ item.parent?.name || '—' }}
          </span>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="item.status ? 'success' : 'error'"
            size="small"
          >
            {{ item.status ? 'Active' : 'Inactive' }}
          </VChip>
        </template>
        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn             
            v-if="$can('delete', 'master.category')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn             
            v-if="$can('update', 'master.category')"
            @click="showCategory(item,'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>  
    <CategoryDrawer
      v-model:isDrawerOpen="isCategoryDrawerVisible"
      :category-data="categoryDrawerData"
      :type="categoryDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="categoryDrawerType = $event"
      @update:category-data="updateCategory"
      @save-category="saveCategory"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Category Deleted."
      confirm-title="Deleted!"
      :data="categoryDeleteData"
      @confirm="deleteCategory"
    />
  </section>
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>

<style lang="scss" scoped>
.category-search-field {
  inline-size: 100%;

  @media (min-width: 600px) {
    inline-size: 20rem;
  }
}

// Rendered as a real button so the row opens from the keyboard too,
// but styled as plain text to sit naturally in the cell.
.category-name {
  padding: 0;
  border: 0;
  background: none;
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  font: inherit;
  transition: color 0.15s ease;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
